<template>
  <div class="tfc-wrap" v-click-outside="closeDropdown">
    <!-- Botão "Tags" + chips das selecionadas. Cada chip tem X individual.
         Click no botão abre dropdown de seleção; click em chip remove. -->
    <button
      type="button"
      class="tfc-trigger"
      :class="{ 'tfc-trigger-open': open, 'tfc-trigger-active': selectedIds.length > 0 }"
      @click="toggle"
    >
      <i class="bx bx-purchase-tag-alt tfc-trigger-icon"></i>
      <span class="tfc-trigger-label">
        {{ selectedIds.length === 0 ? 'Filtrar por tag' : (selectedIds.length === 1 ? '1 tag' : `${selectedIds.length} tags`) }}
      </span>
      <i class="bx bx-chevron-down tfc-trigger-chevron"></i>
    </button>

    <!-- Chips das tags selecionadas (com X individual) -->
    <div v-if="selectedTags.length > 0" class="tfc-chips">
      <span
        v-for="tag in selectedTags"
        :key="tag.id"
        class="tfc-chip"
        :title="tag.name"
      >
        <span class="tfc-chip-name">{{ tag.name }}</span>
        <button
          type="button"
          class="tfc-chip-x"
          :title="`Remover ${tag.name}`"
          @click="removeTag(tag.id)"
        >
          <i class="bx bx-x"></i>
        </button>
      </span>
      <button
        v-if="selectedTags.length > 1"
        type="button"
        class="tfc-clear-all"
        @click="clearAll"
        title="Limpar todas"
      >Limpar</button>
    </div>

    <!-- Dropdown de seleção: campo de busca + lista -->
    <div v-if="open" class="tfc-dropdown">
      <div class="tfc-search-wrap">
        <i class="bx bx-search tfc-search-icon"></i>
        <input
          ref="search"
          type="text"
          v-model="search"
          class="tfc-search-input"
          placeholder="Buscar tag…"
        />
      </div>

      <div class="tfc-list" v-if="filteredTags.length > 0">
        <button
          v-for="tag in filteredTags"
          :key="tag.id"
          type="button"
          class="tfc-item"
          :class="{ 'tfc-item-selected': isSelected(tag.id) }"
          @click="toggleTag(tag.id)"
        >
          <span class="tfc-item-check">
            <i v-if="isSelected(tag.id)" class="bx bx-check"></i>
          </span>
          <span class="tfc-item-name">{{ tag.name }}</span>
        </button>
      </div>
      <div v-else class="tfc-empty">
        Nenhuma tag encontrada
      </div>
    </div>
  </div>
</template>

<script>
const clickOutside = {
  bind (el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event)
      }
    }
    document.addEventListener('click', el.__clickOutsideHandler__)
  },
  unbind (el) {
    document.removeEventListener('click', el.__clickOutsideHandler__)
  }
}

export default {
  name: 'tag-filter-chips',
  directives: { 'click-outside': clickOutside },
  props: {
    tags: { type: Array, default: () => [] }, // [{ id, name }]
    selectedIds: { type: Array, default: () => [] }
  },
  data: () => ({
    open: false,
    search: ''
  }),
  computed: {
    selectedSet () {
      return new Set(this.selectedIds.map(Number))
    },
    selectedTags () {
      return this.tags.filter(t => this.selectedSet.has(Number(t.id)))
    },
    filteredTags () {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.tags
      return this.tags.filter(t => (t.name || '').toLowerCase().includes(q))
    }
  },
  methods: {
    toggle () {
      this.open = !this.open
      if (this.open) {
        this.$nextTick(() => {
          if (this.$refs.search) this.$refs.search.focus()
        })
      }
    },
    closeDropdown () {
      this.open = false
      this.search = ''
    },
    isSelected (id) {
      return this.selectedSet.has(Number(id))
    },
    toggleTag (id) {
      const n = Number(id)
      const next = this.isSelected(n)
        ? this.selectedIds.filter(x => Number(x) !== n)
        : [...this.selectedIds, n]
      this.$emit('change', next)
    },
    removeTag (id) {
      const n = Number(id)
      this.$emit('change', this.selectedIds.filter(x => Number(x) !== n))
    },
    clearAll () {
      this.$emit('change', [])
    }
  }
}
</script>

<style lang="scss" scoped>
.tfc-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tfc-trigger {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #4a5068;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f8f9fc;
    border-color: #d4d8e3;
  }

  &.tfc-trigger-active {
    color: #556ee6;
    border-color: rgba(85, 110, 230, 0.4);
    background: rgba(85, 110, 230, 0.04);
  }

  &.tfc-trigger-open {
    border-color: #556ee6;
    box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
  }
}

.tfc-trigger-icon {
  font-size: 14px;
  color: #8b90a5;
  .tfc-trigger-active & { color: #556ee6; }
}

.tfc-trigger-chevron {
  font-size: 16px;
  color: #8b90a5;
  transition: transform 0.15s ease;

  .tfc-trigger-open & { transform: rotate(180deg); color: #556ee6; }
}

.tfc-chips {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.tfc-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 4px 0 10px;
  background: #fff;
  border: 1px solid rgba(85, 110, 230, 0.3);
  border-radius: 99px;
  font-size: 11px;
  font-weight: 600;
  color: #1a1d26;
  max-width: 180px;
}

.tfc-chip-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.tfc-chip-x {
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  background: transparent;
  border: 0;
  border-radius: 50%;
  color: #8b90a5;
  cursor: pointer;
  transition: all 0.12s ease;
  padding: 0;
  flex-shrink: 0;

  i { font-size: 14px; line-height: 1; }

  &:hover {
    background: rgba(244, 106, 106, 0.12);
    color: #f46a6a;
  }
}

.tfc-clear-all {
  height: 28px;
  padding: 0 10px;
  background: transparent;
  border: 0;
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover { color: #f46a6a; }
}

.tfc-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 240px;
  max-width: 320px;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(26, 29, 38, 0.12);
  padding: 6px;
  z-index: 1000;
  animation: tfc-dropdown-in 0.15s ease-out;
}

@keyframes tfc-dropdown-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.tfc-search-wrap {
  position: relative;
  margin-bottom: 4px;
}

.tfc-search-icon {
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  font-size: 16px;
  color: #8b90a5;
  pointer-events: none;
}

.tfc-search-input {
  width: 100%;
  height: 32px;
  padding: 0 10px 0 30px;
  border: 1px solid #eaecf2;
  border-radius: 6px;
  font-size: 12px;
  color: #1a1d26;

  &:focus {
    outline: none;
    border-color: #556ee6;
    box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.08);
  }

  &::placeholder { color: #8b90a5; }
}

.tfc-list {
  max-height: 260px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #d0d5dd; border-radius: 99px; }
}

.tfc-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-size: 12px;
  color: #1a1d26;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s ease;

  &:hover { background: #f5f6fa; }

  &.tfc-item-selected {
    background: rgba(85, 110, 230, 0.08);
    color: #556ee6;
    font-weight: 600;
  }
}

.tfc-item-check {
  width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
  border: 1.5px solid #d0d5dd;
  border-radius: 4px;
  flex-shrink: 0;

  i { font-size: 14px; color: #fff; }

  .tfc-item-selected & {
    background: #556ee6;
    border-color: #556ee6;
  }
}

.tfc-item-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.tfc-empty {
  padding: 16px 8px;
  text-align: center;
  font-size: 12px;
  color: #8b90a5;
}
</style>
